import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import filter from 'lodash/filter';
import Select from 'react-select';
import { Loading, Button, Input } from '@nextui-org/react';

import { mstore } from '../../store';
import { secureFetch } from '../../lib/api';
import { error, success, warning } from '../../lib/userAlerts';
import checkAccess from '../../lib/checkAccess';
import endpoint from '../../config/endpoint.json';

const TABLE_STYLES = {
  container: {
    width: '90%',
    marginTop: '1rem',
    border: '1px solid #c3c3c3',
    overflow: 'auto',
    maxHeight: 'calc(100vh - 200px)', // Adjust based on your layout
    position: 'relative'
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
  },
  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: 'white',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
  },
  th: {
    fontSize: "1.2rem",
    fontWeight: "400",
    textAlign: 'left',
    padding: '1rem',
    backgroundColor: 'white'
  },
  td: {
    fontSize: "1.0rem",
    fontWeight: "400",
    verticalAlign: "middle"
  }
};

const LOADING_COORDINATOR = [{ value: 'loading.......', label: 'Loading....' }];

const TableHeader = () => (
  <thead style={TABLE_STYLES.thead}>
    <tr>
      {['Sl No', 'Email', 'Employee ID', 'First Name', 'Last Name', 'Coordinator List', 'Actions'].map(header => (
        <th key={header} className='pv2 ph3' style={TABLE_STYLES.th}>
          {header}
        </th>
      ))}
    </tr>
  </thead>
);

const DriverRow = ({ driver, index, coordinatorList, selectedCoordinator, onCoordinatorChange, onSave }) => (
  <tr key={index} className="striped--near-white">
    <td className='pv2 ph3' style={TABLE_STYLES.td}>{index + 1}</td>
    <td className='pv2 ph3' style={TABLE_STYLES.td}>{driver.emailAddress}</td>
    <td className='pv2 ph3' style={TABLE_STYLES.td}>{driver.employeeID}</td>
    <td className='pv2 ph3' style={TABLE_STYLES.td}>{driver.fname}</td>
    <td className='pv2 ph3' style={TABLE_STYLES.td}>{driver.lname}</td>
    <td className='pv2 ph3'>
      <Select
        clearable={false}
        name="coordinator-select"
        styles={{ container: (base) => ({ ...base, width: '200px' }) }}
        placeholder="Search coordinator"
        value={selectedCoordinator}
        onChange={(e) => onCoordinatorChange(driver.id, e)}
        options={coordinatorList}
      />
    </td>
    <td>
      <Button
        ghost
        css={{br: 0}}
        size='sm'
        auto
        onClick={() => onSave(driver.id)}
      >
        Save
      </Button>
    </td>
  </tr>
);

const DriverDetails = () => {
  const [state, setState] = useState({
    driverList: [],
    apiDriverList: [],
    loading: true,
    coordinatorList: LOADING_COORDINATOR,
    coordinatorSelections: {} // Store coordinator selections by driver ID
  });

  const clientId = useSelector(state => state.client ? state.client.id : mstore.getPath(["user", "company_id"]));

  useEffect(() => {
    const initializeData = async () => {
      await checkAccess(clientId);
      await Promise.all([
        getDriverList(),
        getCoordinatorList()
      ]);
    };

    initializeData();
  }, [clientId]);

  const getDriverList = async () => {
    try {
      const [ok, _, json] = await secureFetch(endpoint.getDriverList, {});
      if (ok) {
        const coordinatorSelections = {};
        json.driverList.forEach((driver) => {
          if (driver.coordinator_id) {
            coordinatorSelections[driver.id] = driver.coordinator_id;
          }
        });

        setState(prev => ({
          ...prev,
          driverList: json.driverList,
          apiDriverList: json.driverList,
          loading: false,
          coordinatorSelections
        }));
      } else {
        error('Error occurred while fetching driver list');
      }
    } catch (err) {
      error('Failed to fetch driver list');
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  const getCoordinatorList = async () => {
    try {
      const [ok, _, json] = await secureFetch(endpoint.getCoordinatorList, {});
      if (ok) {
        setState(prev => ({
          ...prev,
          coordinatorList: json.coordinatorList.map(k => ({ 
            label: k.name, 
            value: k.id 
          }))
        }));
      } else {
        error('Error occurred while fetching coordinator list');
      }
    } catch (err) {
      error('Failed to fetch coordinator list');
    }
  };

  const handleCoordinatorChange = (driverId, selectedOption) => {
    setState(prev => ({
      ...prev,
      coordinatorSelections: {
        ...prev.coordinatorSelections,
        [driverId]: selectedOption?.value || ""
      }
    }));
  };

  const handleSave = async (driverId) => {
    const coordinatorId = state.coordinatorSelections[driverId];
    
    if (!coordinatorId) {
      warning("Please select coordinator first");
      return;
    }

    try {
      const [ok] = await secureFetch(endpoint.driverAndCoordinatorMap, {
        coordinator_Id: coordinatorId,
        driver_Id: driverId
      });

      if (ok) {
        success('Driver Coordinator successfully added.');
      } else {
        error('Error occurred while mapping driver to coordinator');
      }
    } catch (err) {
      error('Failed to map driver to coordinator');
    }
  };

  const handleSearch = (event) => {
    const searchValue = event.target.value.toLowerCase();
    const filteredDrivers = filter(state.apiDriverList, driver => {
      return (
        (driver.lname && driver.lname.toLowerCase().includes(searchValue)) ||
        (driver.fname && driver.fname.toLowerCase().includes(searchValue)) ||
        (driver.employeeID && driver.employeeID.toLowerCase().includes(searchValue)) ||
        driver.emailAddress.toLowerCase().includes(searchValue)
      );
    });

    setState(prev => ({ ...prev, driverList: filteredDrivers }));
  };

  return (
    <div className="ml3 mt3">
      <Input
        type="text"
        placeholder="Search by Name, Email or Employee ID"
        onChange={handleSearch}
        clearable
        css={{ $$inputBorderRadius: "0", w: '90%', h: 38 }}
        shadow={false}
        bordered={true}
        rounded={false}
      />
      
      {state.loading && (
        <div className="flex justify-center mt6">
          <Loading size='lg'>Fetching driver details...</Loading>
        </div>
      )}

      {!state.loading && state.apiDriverList.length > 0 && (
        <div style={TABLE_STYLES.container}>
          <table style={TABLE_STYLES.table}>
            <TableHeader />
            <tbody>
              {state.driverList.map((driver, index) => (
                <DriverRow
                  key={driver.id}
                  driver={driver}
                  index={index}
                  coordinatorList={state.coordinatorList}
                  selectedCoordinator={state.coordinatorSelections[driver.id]}
                  onCoordinatorChange={handleCoordinatorChange}
                  onSave={handleSave}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!state.loading && state.driverList.length === 0 && (
        <div className="mt3 f5">No Driver Found.</div>
      )}
    </div>
  );
};

export default DriverDetails;